import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState, Fragment } from "react";
import { fetchAgeGroups, fetchCategories, fetchSkills } from "@/api";
import type { ApiAgeGroup, ApiCategory, ApiSkill } from "@/api/types";
import { getAgeRangeLabel, getCategoryUi } from "@/constants/ui";
import IconRenderer from "@/components/feature/IconRenderer";
import VideoModal from "./components/VideoModal";

export default function Assessment() {
  const { t, i18n } = useTranslation("common");
  const { ageGroupId } = useParams<{ ageGroupId: string }>();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const ageGroupIdNumber = Number(ageGroupId);
  const [ageGroups, setAgeGroups] = useState<ApiAgeGroup[]>([]);
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [skills, setSkills] = useState<ApiSkill[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [checkedSkills, setCheckedSkills] = useState<Set<string>>(new Set());
  const [videoModal, setVideoModal] = useState<{ open: boolean; title: string; videoUrl: string }>({ open: false, title: "", videoUrl: "" });
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const ageGroup = ageGroups.find((g) => g.id === ageGroupIdNumber);
  const ageSkills = useMemo(() => skills, [skills]);

  const activeCategory = useMemo(() => {
    return categories[currentStepIndex]?.key || "";
  }, [categories, currentStepIndex]);

  useEffect(() => {
    if (!ageGroupIdNumber || Number.isNaN(ageGroupIdNumber)) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setHasError(false);

    Promise.all([
      fetchAgeGroups(i18n.language),
      fetchCategories(i18n.language),
      fetchSkills(ageGroupIdNumber, i18n.language),
    ])
      .then(([ageData, categoryData, skillData]) => {
        if (!isMounted) return;
        setAgeGroups(ageData);
        setCategories(categoryData);
        setSkills(skillData);
      })
      .catch(() => {
        if (!isMounted) return;
        setHasError(true);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [ageGroupIdNumber, i18n.language]);

  const skillsByCategory = useMemo(() => {
    const result: Record<string, ApiSkill[]> = {};
    categories.forEach((cat) => {
      result[cat.key] = ageSkills.filter((s) => s.category.key === cat.key);
    });
    return result;
  }, [ageSkills, categories]);

  const toggleSkill = (skillId: string) => {
    const next = new Set(checkedSkills);
    if (next.has(skillId)) {
      next.delete(skillId);
    } else {
      next.add(skillId);
    }
    setCheckedSkills(next);
  };

  const totalSkills = ageSkills.length;
  const checkedCount = checkedSkills.size;
  const progress = totalSkills > 0 ? Math.round((checkedCount / totalSkills) * 100) : 0;

  const handleComplete = () => {
    const scoresByCategory = categories.reduce<Record<string, number>>((acc, cat) => {
      const catSkills = ageSkills.filter((skill) => skill.category.key === cat.key);
      const mastered = catSkills.filter((skill) => checkedSkills.has(skill.id)).length;
      const total = catSkills.length;
      const percentage = total > 0 ? Math.round((mastered / total) * 100) : 0;
      acc[cat.key] = percentage;
      return acc;
    }, {});

    // Store assessment data in session storage for report generation
    const assessmentData = {
      ageGroupId: ageGroupIdNumber,
      checkedSkills: Array.from(checkedSkills),
      totalSkills,
      scores: scoresByCategory,
      timestamp: Date.now(),
    };
    sessionStorage.setItem("assessmentData", JSON.stringify(assessmentData));
    navigate("/report/preview");
  };

  if (hasError || (!isLoading && !ageGroup)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF7]">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-gray-400 mb-4" />
          <p className="text-gray-500">{hasError ? t("dataLoadFailed") : t("ageGroupNotFound")}</p>
          <button
            onClick={() => navigate("/")}
            className="mt-4 px-6 py-2 bg-primary-400 text-white rounded-full text-sm font-medium"
          >
            {t("back")}
          </button>
        </div>
      </div>
    );
  }

  if (isLoading && !ageGroup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF7]">
        <div className="text-center text-gray-500">{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF7] pt-20 md:pt-24 pb-8">
      <div className="w-full px-4 md:px-8 lg:px-16 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate("/")}
              className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
            >
              {isRTL ? (
                <i className="ri-arrow-right-line text-gray-600" />
              ) : (
                <i className="ri-arrow-left-line text-gray-600" />
              )}
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-navy-400">{t("assessmentTitle")}</h1>
              <p className="text-sm text-gray-500">
                {t("ageGroup")}: {ageGroup?.title || t("loading")} —
                {ageGroup ? ` ${getAgeRangeLabel(ageGroup.minMonths, ageGroup.maxMonths)}` : ""} {ageGroup?.maxMonths && ageGroup.maxMonths <= 12 ? t("months") : t("years")}
              </p>
            </div>
          </div>
        </div>

        {/* Stepper Progress Bar */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm mb-6">
          <div className="flex flex-row items-start justify-between gap-2 relative">
            {/* Step 1: Age Selection */}
            <div className="relative flex flex-col items-center text-center flex-1">
              {/* Connecting Line to Step 2 */}
              <div
                className="absolute left-1/2 rtl:left-auto rtl:right-1/2 top-[18px] md:top-[20px] w-full h-0.5 z-0 transition-colors duration-500"
                style={{
                  backgroundColor: categories[0]?.colorHex || "#e5e7eb",
                }}
              />
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm flex-shrink-0 shadow-sm z-10 relative">
                <i className="ri-check-line text-base md:text-lg" />
              </div>
              <div className="hidden md:flex flex-col items-center mt-3">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{t("step1Title")}</p>
                <p className="text-xs md:text-sm font-bold text-navy-400 mt-1">{ageGroup?.title}</p>
              </div>
            </div>

            {/* Category Steps */}
            {categories.map((cat, idx) => {
              const ui = getCategoryUi(cat.key);
              const isCurrent = activeCategory === cat.key;
              const currentCategoryIdx = categories.findIndex((c) => c.key === activeCategory);
              const isPast = currentCategoryIdx > idx;
              const catSkills = skillsByCategory[cat.key] || [];
              const catChecked = catSkills.filter((s) => checkedSkills.has(s.id)).length;
              const percentage = catSkills.length > 0 ? Math.round((catChecked / catSkills.length) * 100) : 0;

              return (
                <Fragment key={cat.id}>
                  <div className="relative flex flex-col items-center text-center flex-1">
                    {/* Connecting Line to Next Step */}
                    {idx < categories.length - 1 && (
                      <div
                        className="absolute left-1/2 rtl:left-auto rtl:right-1/2 top-[18px] md:top-[20px] w-full h-0.5 z-0 transition-colors duration-500"
                        style={{
                          backgroundColor: currentCategoryIdx >= idx + 1 ? categories[idx + 1].colorHex : "#e5e7eb",
                        }}
                      />
                    )}

                    <div
                      className="w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 flex-shrink-0 z-10 relative"
                      style={{
                        backgroundColor: isCurrent || isPast ? cat.colorHex : "#f3f4f6",
                        color: isCurrent || isPast ? "#ffffff" : "#9ca3af",
                        boxShadow: isCurrent ? `0 0 14px ${cat.colorHex}60` : "none",
                      }}
                    >
                      {isPast ? (
                        <i className="ri-check-line text-base md:text-lg" />
                      ) : (
                        <IconRenderer
                          iconUrl={cat.iconUrl}
                          fallbackIcon={ui.icon}
                          color={isCurrent ? "#ffffff" : "#9ca3af"}
                          size={18}
                        />
                      )}
                    </div>
                    <div className="hidden md:flex flex-col items-center mt-3">
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                        {idx === 0 && t("physical")}
                        {idx === 1 && t("cognitive")}
                        {idx === 2 && t("social")}
                        {idx > 2 && cat.name}
                      </p>
                      <p className="text-xs md:text-sm font-bold text-navy-400 mt-1">{cat.name}</p>
                      {(isCurrent || isPast) && (
                        <p className="text-[11px] font-semibold mt-1" style={{ color: cat.colorHex }}>
                          {catChecked}/{catSkills.length} ({percentage}%)
                        </p>
                      )}
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>

          {/* Mobile-only Active Category Summary */}
          <div className="md:hidden mt-4 pt-4 border-t border-gray-100 flex flex-col items-center text-center">
            {categories[currentStepIndex] && (() => {
              const cat = categories[currentStepIndex];
              const catSkills = skillsByCategory[cat.key] || [];
              const catChecked = catSkills.filter((s) => checkedSkills.has(s.id)).length;
              const percentage = catSkills.length > 0 ? Math.round((catChecked / catSkills.length) * 100) : 0;
              return (
                <>
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    {currentStepIndex === 0 && t("physical")}
                    {currentStepIndex === 1 && t("cognitive")}
                    {currentStepIndex === 2 && t("social")}
                    {currentStepIndex > 2 && cat.name}
                  </span>
                  <span className="text-sm font-bold text-navy-400 mt-0.5">
                    {cat.name}
                  </span>
                  <span className="text-xs font-semibold mt-0.5" style={{ color: cat.colorHex }}>
                    {catChecked}/{catSkills.length} ({percentage}%)
                  </span>
                </>
              );
            })()}
          </div>
        </div>

        {/* Active Step Progress */}
        {categories[currentStepIndex] && (
          <div className="bg-white rounded-xl p-4 md:p-5 border border-gray-100 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs md:text-sm font-medium text-gray-600">
                {t("progress")}: {skillsByCategory[activeCategory]?.filter((s) => checkedSkills.has(s.id)).length || 0} {t("of")} {skillsByCategory[activeCategory]?.length || 0} {t("skillsChecked")}
              </span>
              <span className="text-xs md:text-sm font-bold" style={{ color: categories[currentStepIndex].colorHex }}>
                {skillsByCategory[activeCategory]?.length > 0
                  ? Math.round((skillsByCategory[activeCategory]?.filter((s) => checkedSkills.has(s.id)).length / skillsByCategory[activeCategory]?.length) * 100)
                  : 0}%
              </span>
            </div>
            <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${
                    skillsByCategory[activeCategory]?.length > 0
                      ? Math.round((skillsByCategory[activeCategory]?.filter((s) => checkedSkills.has(s.id)).length / skillsByCategory[activeCategory]?.length) * 100)
                      : 0
                  }%`,
                  backgroundColor: categories[currentStepIndex].colorHex
                }}
              />
            </div>
          </div>
        )}

        {/* Skills List */}
        <div className="space-y-3 mb-8">
          {(skillsByCategory[activeCategory] || []).map((skill, index) => {
            const isChecked = checkedSkills.has(skill.id);
            const activeColor = categories[currentStepIndex]?.colorHex || "#E8927C";

            return (
              <div
                key={skill.id}
                className="bg-white rounded-xl p-4 md:p-5 border-2 transition-all duration-300"
                style={{
                  borderColor: isChecked ? `${activeColor}50` : "#f3f4f6",
                  backgroundColor: isChecked ? `${activeColor}08` : "#ffffff",
                  animationDelay: `${index * 50}ms`
                }}
              >
                <div className="flex items-start gap-3 md:gap-4">
                  <button
                    onClick={() => toggleSkill(skill.id)}
                    className="w-6 h-6 md:w-7 md:h-7 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-200"
                    style={{
                      backgroundColor: isChecked ? activeColor : "transparent",
                      borderColor: isChecked ? activeColor : "#d1d5db",
                    }}
                  >
                    {isChecked && <i className="ri-check-line text-white text-sm" />}
                  </button>

                  <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <IconRenderer
                      iconUrl={skill.iconUrl}
                      fallbackIcon={getCategoryUi(skill.category.key).icon}
                      color={skill.category.colorHex}
                      size={16}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm md:text-base leading-relaxed ${isChecked ? "text-gray-900 font-medium" : "text-gray-800"}`}>
                      {skill.title}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {skill.videoUrl && (
                      <button
                        onClick={() => setVideoModal({ open: true, title: skill.title, videoUrl: skill.videoUrl })}
                        className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors group"
                        title={t("watchVideo")}
                      >
                        <i className="ri-video-line text-gray-500 group-hover:text-primary-500 text-sm" />
                      </button>
                    )}
                    <span
                      className="text-xs font-medium px-2 py-1 rounded-full"
                      style={{
                        backgroundColor: isChecked ? `${activeColor}15` : "#f3f4f6",
                        color: isChecked ? activeColor : "#9ca3af",
                      }}
                    >
                      {isChecked ? t("skillMastered") : t("skillNotMastered")}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center gap-4 pb-8">
          <button
            onClick={() => {
              if (currentStepIndex === 0) {
                navigate("/");
              } else {
                setCurrentStepIndex((prev) => prev - 1);
              }
            }}
            className="px-6 py-3 rounded-full border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 font-medium text-sm md:text-base shadow-sm transition-all duration-300 whitespace-nowrap flex items-center gap-2"
          >
            {isRTL ? <i className="ri-arrow-right-line" /> : <i className="ri-arrow-left-line" />}
            {currentStepIndex === 0 ? t("back") : t("back")}
          </button>

          <button
            onClick={() => {
              if (currentStepIndex < categories.length - 1) {
                setCurrentStepIndex((prev) => prev + 1);
              } else {
                handleComplete();
              }
            }}
            className="px-8 py-3.5 rounded-full font-semibold text-sm md:text-base hover:scale-[1.03] shadow-lg transition-all duration-300 whitespace-nowrap flex items-center gap-2 text-white"
            style={{
              backgroundColor: categories[currentStepIndex]?.colorHex || "#E8927C",
              boxShadow: `0 4px 14px ${(categories[currentStepIndex]?.colorHex || "#E8927C")}40`
            }}
          >
            {currentStepIndex === categories.length - 1 ? t("completeAssessment") : t("next")}
            {isRTL ? <i className="ri-arrow-left-line" /> : <i className="ri-arrow-right-line" />}
          </button>
        </div>
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={videoModal.open}
        onClose={() => setVideoModal({ open: false, title: "", videoUrl: "" })}
        title={videoModal.title}
        videoUrl={videoModal.videoUrl}
      />
    </div>
  );
}